import { Alert } from '@api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postRead } from '@terminal/services/alerts/event-alerts-service';
import { optimisticQueriesUpdate } from '@terminal/utils/optimistic-updates/optimistic-updates';
import {
  EventAlertsPaginatedResponse,
  QUERY_KEY_EVENT_ALERTS,
  UNREAD_COUNT_QUERY_KEY,
} from './event-alerts-query-keys';
import { updateAlertsFavicon } from './updateAlertsFavicon';

export const usePostRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => postRead({ ids }),
    onMutate: async (ids: string[]) => {
      const idSet = new Set(ids);

      const { previousData: previousAlertQueries } =
        await optimisticQueriesUpdate<EventAlertsPaginatedResponse>({
          queryClient,
          queryKey: [QUERY_KEY_EVENT_ALERTS],
          updateFn: oldData => ({
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              ...(page.alerts && {
                alerts: page.alerts.map((alert: Alert) =>
                  idSet.has(alert.id) ? { ...alert, read: !alert.read } : alert
                ),
              }),
            })),
          }),
        });

      return { previousAlertQueries };
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_EVENT_ALERTS],
        refetchType: 'none',
      });
      await queryClient.refetchQueries({
        queryKey: UNREAD_COUNT_QUERY_KEY,
      });
      updateAlertsFavicon();
    },
    onError: (_err, _ids, context) => {
      if (context?.previousAlertQueries) {
        context.previousAlertQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
};
